import { HelpTicket, HelpTicketCreateRequest, HelpTicketUpdateRequest } from "@bluefalcon/clients-management-client-fetch";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useEffect } from "react";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Button,
    FormControl,
    FormLabel,
    Input,
    IconButton,
    Box,
} from "@chakra-ui/react";
import { getIDFromJWT, getToken } from "@bluefalcon/utils-package";
import { BF_TOKEN } from "../constants/cookies.constants";
import { useConfirmDelete } from "../hooks/useConfirmDelete";
import { helpTicketService } from "../services";
import { LoadingSpinner } from "../components/base/loading-spinner/loading-spinner.comp";

type AddEditHelpTicketModalProps = {
    isOpen: boolean;
    helpTicket?: HelpTicket;
    handleAddHelpTicket: (data: HelpTicketCreateRequest) => Promise<void>;
    handleEditHelpTicket: (helpTicketId: number, data: HelpTicketUpdateRequest) => Promise<void>;
    onDelete: () => void;
    onClose: () => void;
};

export const AddEditHelpTicketModal = ({
    isOpen,
    helpTicket,
    handleAddHelpTicket,
    handleEditHelpTicket,
    onClose,
    onDelete,
}: AddEditHelpTicketModalProps) => {
    const [details, setDetails] = useState(helpTicket?.details ?? "");
    const [title, setTitle] = useState(helpTicket?.title ?? "");
    const [isLoading, setIsLoading] = useState(false);
    const userId = getIDFromJWT(getToken(BF_TOKEN)!);
    const { confirmDelete, ConfirmDeleteModal } = useConfirmDelete();

    useEffect(() => {
        setDetails(helpTicket?.details ?? "");
        setTitle(helpTicket?.title ?? "");
    }, [helpTicket]);

    const handleDeleteHelpTicket = async () => {
        if (!helpTicket) {
            return;
        };
        setIsLoading(true);
        await helpTicketService.deleteHelpTicket(helpTicket.id);
        onDelete();
        setIsLoading(false);
        onCloseModal()
    };

    const handleSubmit = async () => {
        if (helpTicket && details.trim() && title.trim()) {
            await handleEditHelpTicket(helpTicket.id, { details: details.trim(), title: title.trim() });
        } else if (details.trim()) {
            await handleAddHelpTicket({ details: details.trim(), title: title.trim(), fromUserId: userId });
        }
        onCloseModal();
    };

    const handleDelete = async () => {
        confirmDelete(handleDeleteHelpTicket, 'Ticket');
    };

    const onCloseModal = () => {
        setDetails("");
        setTitle("");
        onClose();
    };

    if (isLoading) {
        return <LoadingSpinner />
    }

    return (
        <Box>
            <Modal isOpen={isOpen} onClose={onCloseModal}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{helpTicket ? "Edit Ticket" : "Add Ticket"}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl mb={4}>
                            <FormLabel htmlFor="title">Ticket Name</FormLabel>
                            <Input
                                id="title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Enter title"
                            />
                        </FormControl>
                        <FormControl mb={4}>
                            <FormLabel htmlFor="details">Ticket Details</FormLabel>
                            <Input
                                type="text"
                                id="details"
                                value={details}
                                onChange={(e) => setDetails(e.target.value)}
                                placeholder="Enter details"
                            />
                        </FormControl>
                    </ModalBody>

                    <ModalFooter>
                        {helpTicket && (
                            <IconButton
                                aria-label="Delete Ticket"
                                icon={<FontAwesomeIcon icon={faTrashCan} />}
                                variant="outline"
                                onClick={handleDelete}
                                mr="auto"
                            />
                        )}
                        <Button variant="outline" onClick={onCloseModal}>
                            Close
                        </Button>
                        <Button isDisabled={(!details.trim() || !title.trim())} colorScheme="teal" ml={3} onClick={handleSubmit}>
                            {helpTicket ? "Edit Ticket" : "Add Ticket"}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            <ConfirmDeleteModal />
        </Box>
    );
};
